import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Plus, 
  Download, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  FileText,
  PieChart,
  Building2
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FinancialAnalytics } from "./components/FinancialAnalytics";
import { BudgetManagement } from "./components/BudgetManagement";
import { GrantsManagement } from "./components/GrantsManagement";
import { TransactionsTable } from "./components/TransactionTable";
import { useTranslation } from 'react-i18next';


interface FinancialData {
  income: {
    total: number;
    categories: {
      donations: number;
      grants: number;
      activities: number;
      partnerships: number;
    };
    trend: number;
  };
  expenses: {
    total: number;
    categories: {
      operations: number;
      conservation: number;
      staff: number;
      marketing: number;
    };
    trend: number;
  };
  budget: {
    total: number;
    used: number;
    remaining: number;
  };
}

const mockFinancialData: FinancialData = {
  income: {
    total: 1250000,
    categories: {
      donations: 450000,
      grants: 500000,
      activities: 200000,
      partnerships: 100000
    },
    trend: 12
  },
  expenses: {
    total: 980000,
    categories: {
      operations: 300000,
      conservation: 400000,
      staff: 200000,
      marketing: 80000
    },
    trend: 8
  },
  budget: {
    total: 1500000,
    used: 980000,
    remaining: 520000
  }
};

export function FundingDashboard() {
  //const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("this-month");
  //const [activeTab, setActiveTab] = useState("overview");
  const { t } = useTranslation('business');

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">{t('financialManagement')}</h1>
          <p className="text-gray-500 mt-2">{t('trackAndOptimizeFinances')}</p>
        </div>
        <div className="flex gap-4">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('selectPeriod')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">{t('thisMonth')}</SelectItem>
              <SelectItem value="last-month">{t('lastMonth')}</SelectItem>
              <SelectItem value="this-quarter">{t('thisQuarter')}</SelectItem>
              <SelectItem value="this-year">{t('thisYear')}</SelectItem>
              <SelectItem value="custom">{t('customRange')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            {t('exportFinancialReport')}
          </Button>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {t('recordTransaction')}
          </Button>
        </div>
      </div>

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('totalIncome')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-bold">${mockFinancialData.income.total.toLocaleString()}</div>
              <div className="flex items-center text-green-600">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span className="text-sm">+{mockFinancialData.income.trend}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('totalExpenses')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-bold">${mockFinancialData.expenses.total.toLocaleString()}</div>
              <div className="flex items-center text-red-600">
                <TrendingDown className="w-4 h-4 mr-1" />
                <span className="text-sm">+{mockFinancialData.expenses.trend}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('budgetRemaining')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">${mockFinancialData.budget.remaining.toLocaleString()}</div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-primary h-2.5 rounded-full" 
                  style={{ width: `${(mockFinancialData.budget.used / mockFinancialData.budget.total) * 100}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              {t('activeGrants')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-bold">12</div>
              <Button variant="ghost" size="sm" className="text-primary">
                {t('viewAll')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card className="bg-white">
        <CardContent className="pt-6">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-12 mb-4">
              <TabsTrigger 
                value="transactions"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <FileText className="w-4 h-4" />
                {t('transactions')}
              </TabsTrigger>
              <TabsTrigger 
                value="analytics"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <PieChart className="w-4 h-4" />
                {t('financialAnalytics')}
              </TabsTrigger>
              <TabsTrigger 
                value="budgets"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <DollarSign className="w-4 h-4" />
                {t('budgets')}
              </TabsTrigger>
              <TabsTrigger 
                value="grants"
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              >
                <Building2 className="w-4 h-4" />
                {t('grantsAndDonations')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="transactions">
              {/* Transactions Table Component */}
              <TransactionsTable />
            </TabsContent>

            <TabsContent value="analytics">
              {/* Analytics Component */}
              <FinancialAnalytics />
            </TabsContent>

            <TabsContent value="budgets">
              {/* Budgets Component */}
              <BudgetManagement />
            </TabsContent>

            <TabsContent value="grants">
              {/* Grants & Donations Component */}
              <GrantsManagement />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// Additional components would be defined here or in separate files